import { useEffect, useState } from "react";

import { SimpleChipItem, Textfield } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { useBrandDraft } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/hooks/useBrandDraft";
import { BrandAutocompleteCombobox } from "app/pages/digital-marketing/generative-ai/shared/BrandAutocompleteCombobox/BrandAutocompleteCombobox";

import type { ITrackedBrandNamesProps } from "./types";

const MAX_VISIBLE_ROWS = 3;

type ITrackedBrandNamesFieldProps = Omit<ITrackedBrandNamesProps, "isLoading">;

export const BrandListField = ({
    brandNames = [],
    onBrandsChange,
    error: externalError,
}: ITrackedBrandNamesFieldProps) => {
    const [selectedBrands, setSelectedBrands] = useState<string[]>(brandNames);
    const {
        brandFieldRef,
        brandDraft,
        setBrandDraft,
        trimmedBrand,
        brandValidationError,
        brandDraftErrorMessage,
        canAddBrand,
        addBrand,
        commitBrand,
        onBrandKeyDown,
    } = useBrandDraft({
        existingBrands: selectedBrands,
        onAdd: (brand) => setSelectedBrands([...selectedBrands, brand]),
    });

    const error = brandDraftErrorMessage || externalError || "";

    useEffect(() => {
        onBrandsChange?.(selectedBrands);
    }, [selectedBrands]);

    useEffect(() => {
        if (!selectedBrands.length) {
            const virtualBrandsList = trimmedBrand && !brandValidationError ? [trimmedBrand] : [];
            onBrandsChange?.(virtualBrandsList);
        }
    }, [selectedBrands, trimmedBrand, brandValidationError]);

    return (
        <>
            <BrandAutocompleteCombobox
                query={brandDraft}
                onSelect={commitBrand}
                showTypedAsOption
                dataAutomation="llm-setup-brand"
                excludedBrands={selectedBrands}
                disabled={!!brandDraftErrorMessage}
            >
                <Textfield
                    ref={brandFieldRef}
                    dataAutomation="llm-setup-brand-input"
                    placeholder={translate("llm.setup.modal.brand.placeholder")}
                    ApplyButtonText={translate("global.add")}
                    onChange={setBrandDraft}
                    onApplyClick={canAddBrand ? addBrand : undefined}
                    error={!!error}
                    errorMessage={error}
                    onKeyDown={onBrandKeyDown}
                />
            </BrandAutocompleteCombobox>
            <Flex
                flexWrap="wrap"
                overflow="auto"
                maxHeight={`${40 * MAX_VISIBLE_ROWS}px`}
            >
                {selectedBrands.map((brand) => (
                    <SimpleChipItem
                        key={brand}
                        text={brand}
                        onCloseItem={() => setSelectedBrands(selectedBrands.filter((b) => b !== brand))}
                    />
                ))}
            </Flex>
        </>
    );
};
